/**
 * The slice of the Web Storage API this app depends on, and a total way of
 * getting hold of one.
 *
 * A type rather than a direct `localStorage` reference, so modules that
 * persist something take the handle as a parameter defaulting to
 * `resolveLocalStorage`. That keeps them importable under vitest's node
 * environment and
 * testable under vitest's node environment, neither of which has
 * `localStorage`, without mocking a global.
 *
 * Every persisted feature should default its storage parameter to
 * `resolveLocalStorage` and build its key with `storageKey`, both from
 * here, rather than reimplementing either.
 */
import { STORAGE_PREFIX } from "./site";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The browser's `localStorage`, or null where there isn't one. The
 * try/catch matters — in some privacy modes merely accessing
 * `localStorage` throws.
 */
export function resolveLocalStorage(): StorageLike | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

/**
 * Namespace a storage key under STORAGE_PREFIX:
 * `storageKey("install.v1")` → `"template.install.v1"`. Routing every key
 * through this function avoids what a hand-typed key could forget.
 */
export function storageKey(suffix: string): string {
  return `${STORAGE_PREFIX}.${suffix}`;
}
